import axios from 'axios';
import { env } from '@config/env';
import { useAuthStore } from '../stores/authStore';

/** The API clients' own limit: a hung refresh or logout must not hold every waiting call, or sign-out. */
const AUTH_REQUEST_TIMEOUT_MS = 20_000;

/**
 * Raw (interceptor-free) target for the regional tenant auth endpoints.
 *
 * CRITICAL: these calls can't go through the `tenantApi` instance (a 401 would recurse through
 * its interceptor), so they must attach `x-tenant-id` themselves. Without it, `tenantMiddleware`
 * falls back to reading the tenant from the API hostname (e.g. `ip4x-za.sudosky.com` →
 * `ip4x-za`), resolves the wrong/no tenant, and the call fails — which, for refresh, logs the
 * member out on every access-token expiry.
 */
function regionalAuthRequest(path: string): { url: string; headers: Record<string, string> } {
  const auth = useAuthStore.getState();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (env.appTemplateId) headers['x-app-template-id'] = env.appTemplateId;
  if (auth.tenantId) headers['x-tenant-id'] = auth.tenantId;

  return { url: `${auth.regionalApiUrl || env.apiBaseUrl}${path}`, headers };
}

/**
 * Exchange a refresh token for a fresh access/refresh pair.
 *
 * Both login paths ultimately mint **tenant-issued** tokens:
 *   - authMode 'tenant'  → direct `POST /auth/login` on the regional API.
 *   - authMode 'core'    → Core `/portal-auth/login` *proxies* to the same
 *                          Tenant API and returns its tokens.
 * So refresh always targets the regional tenant `POST /auth/refresh`.
 */
export async function refreshAuthTokens(
  refreshToken: string,
): Promise<{ accessToken: string; refreshToken?: string }> {
  const { url, headers } = regionalAuthRequest('/auth/refresh');
  const { data } = await axios.post(url, { refreshToken }, { headers, timeout: AUTH_REQUEST_TIMEOUT_MS });

  return { accessToken: data.accessToken, refreshToken: data.refreshToken };
}

/** End the server-side session behind a refresh token, so a copy of it stops working. */
export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  const { url, headers } = regionalAuthRequest('/auth/logout');
  await axios.post(url, { refreshToken }, { headers, timeout: AUTH_REQUEST_TIMEOUT_MS });
}
